import React, { useMemo, useState } from 'react';

import type { EditorState } from 'lexical';
import { twMerge } from 'tailwind-merge';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useLexicalMentionsMenuContainerMarginClassName } from '@components/Lexical/useLexicalMentionsMenuBottomPadding';
import { LexicalUtils } from '@components/Lexical/utils';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { FeedPostModalMain } from '@components/modals/FeedPostModal/FeedPostModalMain';
import {
  FeedPostSelectResource,
  PostResource,
} from '@components/modals/FeedPostModal/SelectResource';
import { useFeedPost } from '@components/modals/FeedPostModal/useFeedPost';
import { useModal } from '@context/modal';
import { POST_MAX_CHARACTERS } from '@utils/constants';
import type { FeedPostResource } from './types';

export enum FeedPostModalStep {
  Main,
  ResourceSelect,
}

export function FeedPostModal({
  sourceLocation,
  preselectedResource,
}: {
  sourceLocation: PostModalSource;
  preselectedResource: FeedPostResource | null;
}) {
  const { closeModal } = useModal();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [step, setStep] = useState(FeedPostModalStep.Main);
  const [postCreated, setPostCreated] = useState(false);
  const [selectedTab, setTab] = React.useState<PostResource>(PostResource.RELEASE);

  const [selectedResource, setSelectedResource] = useState<FeedPostResource | null>(null);

  const [mainResource, setMainResource] = useState<FeedPostResource | null>(preselectedResource);

  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [lexicalState, setLexicalState] = useState<EditorState>();
  const postTextLength = useMemo(
    () => LexicalUtils.getNodeTextLength(LexicalUtils.getRootNode(lexicalState)),
    [lexicalState],
  );
  const isTextTooLong = postTextLength > POST_MAX_CHARACTERS;
  const submitEnabled = postTextLength > 0 && !!mainResource && !isTextTooLong;

  const {
    createReleaseIsLoading,
    createPlaylistIsLoading,

    createReleasePost,
    createPlaylistPost,
  } = useFeedPost();

  const handleSubmitClicked = useStableCallback(async () => {
    if (!mainResource) {
      return;
    }

    if (mainResource.type === 'release') {
      createReleasePost({
        release: mainResource.release,
        lexicalContent: lexicalState?.toJSON(),
        sourceLocation,
        onPostCreated: () => {
          setPostCreated(true);
        },
      });
    } else {
      createPlaylistPost({
        playlistId: mainResource.playlistId,
        lexicalContent: lexicalState?.toJSON(),
        sourceLocation,
        onPostCreated: () => {
          setPostCreated(true);
        },
      });
    }
  });

  const title = useMemo(
    () => (step === FeedPostModalStep.Main ? 'Write a Post' : 'Select sound or playlist'),
    [step],
  );

  React.useEffect(() => {
    if (!bodyRef.current) {
      return;
    }
    setHasOverflow(bodyRef.current.clientHeight < bodyRef.current.scrollHeight);
  }, [bodyRef]);

  const stepMainCTA = (
    <div className="flex w-full gap-3 pb-3">
      <Button
        fullWidth
        onClick={() => {
          // if they have post text or a sound selected, show a confirmation modal
          if (postTextLength && confirm('Are you sure? You changes will not be saved.') === false) {
            return;
          }

          closeModal();
        }}
        label="Cancel"
        disabled={postCreated}
        className="flex-1"
        variant="secondary"
      />
      <Button
        fullWidth
        disabled={!submitEnabled || postCreated}
        label="Post"
        className="flex-1"
        loading={createReleaseIsLoading || createPlaylistIsLoading}
        variant="primary"
        onClick={handleSubmitClicked}
        type="submit"
      />
    </div>
  );
  const stepSoundSelectCTA = (
    <div className="relative z-above1 flex w-full gap-3 pb-3 ">
      <Button
        variant="secondary"
        fullWidth
        className="flex-1"
        label="Back"
        onClick={() => {
          setStep(FeedPostModalStep.Main);
        }}
      />
      <Button
        variant="primary"
        fullWidth
        className="flex-1"
        label="Select"
        disabled={!selectedResource}
        onClick={() => {
          // don't really need this because the button is disabled
          // when there is no selected release
          // but just in case
          if (selectedResource) {
            setMainResource(selectedResource);
            setStep(FeedPostModalStep.Main);
          }
        }}
      />
    </div>
  );

  const extraPaddingClassName = useLexicalMentionsMenuContainerMarginClassName();

  return (
    <StandardModal
      ref={bodyRef}
      mobileFullScreen
      closeModal={closeModal}
      bodyClassName={twMerge('relative flex flex-col px-0')}
      containerClassName="pb-0 h-full md:h-fit"
      overlayClassName="min-h-[-webkit-fill-available]"
      bodyWithoutPadding
      bodyWithoutPaddingBottom
      showHeaderShadow={hasOverflow}
      title={title}
      callToAction={step === FeedPostModalStep.Main ? stepMainCTA : stepSoundSelectCTA}
      body={
        <div className="relative flex-1 overflow-x-hidden">
          <div
            className={twMerge(
              `flex h-full flex-col gap-6 px-4  pb-4 transition-transform duration-200 ease-out lt:px-6 lt:pb-6`,
              step === FeedPostModalStep.Main ? `translate-x-0` : `-translate-x-full`,
              extraPaddingClassName,
            )}
          >
            <FeedPostModalMain
              allowEdit={!preselectedResource}
              onEditResource={() => {
                setStep(FeedPostModalStep.ResourceSelect);
              }}
              onChangeLexicalState={setLexicalState}
              resource={mainResource}
            />
          </div>
          <div
            className={`absolute inset-0 flex flex-col gap-6 pb-4 transition-transform duration-200 ease-out  lt:pb-6 ${
              step === FeedPostModalStep.Main ? `translate-x-full` : `translate-x-0`
            }`}
          >
            <FeedPostSelectResource
              selectedResource={selectedResource}
              setSelectedResource={setSelectedResource}
              selectedTab={selectedTab}
              setTab={setTab}
              step={step}
            />
          </div>
        </div>
      }
    />
  );
}
